/**
 * Is the signed-in session a child's? One answer for every surface that must
 * treat a child differently (no price, no billing notice, no parental controls).
 *
 * Two signals, because neither covers every child (failure-patterns.md
 * Patterns A and I):
 *  - a SELF-registered child signs in as their own account: role "child", no
 *    child display name;
 *  - a parent-managed child profile signs in with a legacy ``child_session``
 *    token that carries NO role claim: role undefined, but App sets the child's
 *    display name.
 * Keying on role alone let a parent-managed 7-year-old meet the paywall
 * (first-ten-minutes walk, 2026-10-22). App provides the display name here
 * once, so AppShell, BillingChrome and useAccountNotice cannot drift.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { AuthContext } from "./useAuth";
import { useViewerKindFromApi } from "./viewerKind";

const ChildDisplayNameCtx = createContext<string>("");

export function ChildSessionProvider({
  childDisplayName,
  children,
}: {
  childDisplayName: string | null | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <ChildDisplayNameCtx.Provider value={childDisplayName ?? ""}>{children}</ChildDisplayNameCtx.Provider>
  );
}

/** ``childDisplayName`` overrides the provided one (AppShell has it as a prop). */
export function useIsChildSession(childDisplayName?: string | null): boolean {
  const role = useContext(AuthContext)?.role;
  const provided = useContext(ChildDisplayNameCtx);
  // Where no role is known at all (the share page's island), the API's answer (U18).
  const fromApi = useViewerKindFromApi();
  return role === "child" || !!(childDisplayName ?? provided) || (!role && fromApi === "child");
}

/**
 * The same answer, or null while neither signal has arrived: no role claim and no child
 * display name yet. A grown-up's token always carries a role, so null is a session not yet
 * known, never a grown-up. A surface that does something only for a grown-up (fetch the
 * family roster, hold the town for the parent's welcome) waits on null rather than guess:
 * on a first render a parent-managed child's name can still be on its way.
 */
export function useSessionKind(childDisplayName?: string | null): "child" | "grownup" | null {
  const role = useContext(AuthContext)?.role;
  const provided = useContext(ChildDisplayNameCtx);
  const fromApi = useViewerKindFromApi();
  if (role === "child" || !!(childDisplayName ?? provided)) return "child";
  // No role and no child name: the share page's island, which knows only what the API said (U18).
  return role ? "grownup" : fromApi;
}
